import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Alert,
  Button,
  Select,
  DataTable,
  Skeleton,
  Modal,
  EmptyState,
  ErrorBoundary,
  useToast,
  FilterBar,
  SavedFiltersMenu,
  ListSummaryBar,
} from '@bbpos/ui';
import type { ColumnDef, FilterBarFacet } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatDocAmount } from '@/utils/purchase-currency';
import { GLASS } from '@/constants/design-tokens';
import { useListFilterBar } from '@/features/saved-filters';
import { downloadFile } from '@/lib/download-file';
import {
  usePurchaseOrders,
  usePurchaseOrderStats,
  useAutoGeneratePO,
  POStatusBadge,
  PoKpi,
} from '@/features/purchase-orders';
import type { PurchaseOrderListItem, PurchaseOrderStatus } from '@/features/purchase-orders';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { locationService } from '@/services/location.service';
import { purchaseOrdersApi } from '@/features/purchase-orders/api';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ClipboardIcon,
  TruckIcon,
  CheckCircleIcon,
  ClockIcon,
  PlusIcon,
  BoltIcon,
  EyeIcon,
} from './icons';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ORDERED', label: 'Ordered' },
  { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' },
  { value: 'RECEIVED', label: 'Received' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// T27: the shared filter bar. Keys are the list endpoint's own parameters;
// location_id stays the page's own scope control (below), saved with the rest.
export const PO_FILTER_KEYS = [
  'supplier_name', 'mobile', 'po_number', 'search', 'status', 'start_date', 'end_date', 'location_id',
];
const PO_FACETS: FilterBarFacet[] = [
  { key: 'supplier_name', label: 'Supplier' },
  { key: 'mobile', label: "Supplier's mobile", kind: 'phone' },
  { key: 'po_number', label: 'PO no.', kind: 'number' },
  { key: 'search', label: 'PO no. or supplier' },
];
const PO_DATE = { fromKey: 'start_date', toKey: 'end_date' };

// Explicit "no location scope" value for the location_id URL param. Without it,
// clearing the dropdown would just fall back to the header's location instead of
// widening to all locations.
const ALL_LOCATIONS = 'all';

// ─── Header ─────────────────────────────────────────────────────────────────

// Shared by the loaded, empty and failed states so all three keep the same chrome —
// the page below only swaps what sits under it. Auto-Generate is opt-in: it's absent
// on the empty and failed states, where there is no loaded list to generate against.
function PoListHeader({
  isManager,
  isOwner,
  onNew,
  onAutoGenerate,
  onGenerateDaily,
  onEditDaily,
  generatingDaily,
}: {
  isManager: boolean;
  isOwner: boolean;
  onNew: () => void;
  onAutoGenerate?: () => void;
  onGenerateDaily: () => void;
  onEditDaily: () => void;
  generatingDaily: boolean;
}) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 to-brand-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(13,148,136,0.45)] mb-6 animate-fade-in">
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
      <div className="relative flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-white">Purchase Orders</h1>
          <p className="text-teal-100 mt-1">Manage vendor procurement and track deliveries</p>
        </div>
        {isManager && (
          <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
            {/* One-click: place the saved DAILY PO list as an ORDERED purchase order. */}
            <Button
              variant="secondary"
              onClick={onGenerateDaily}
              loading={generatingDaily}
              className="!bg-white !text-teal-700 hover:!bg-white/90 font-semibold"
            >
              <BoltIcon className="h-4 w-4 mr-1.5" />
              Generate DAILY PO
            </Button>
            {isOwner && (
              <Button
                variant="secondary"
                onClick={onEditDaily}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                Edit DAILY PO
              </Button>
            )}
            {onAutoGenerate && (
              <Button
                variant="secondary"
                onClick={onAutoGenerate}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <BoltIcon className="h-4 w-4 mr-1.5" />
                Auto-Generate
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={onNew}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              New PO
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

export function PurchaseOrderListContent() {
  const currency = useTenantCurrency();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);

  const page = Number(searchParams.get('page') ?? '1');
  const locationParam = searchParams.get('location_id') ?? '';

  // The on-page Location dropdown owns this page's scope; the header's global
  // location is only the default when the dropdown hasn't been touched. The list
  // AND all four KPI cards read this, so the page always states — and lets the
  // user change — the location its numbers describe.
  const effectiveLocationId =
    locationParam === ALL_LOCATIONS ? undefined : locationParam || selectedLocationId;

  // Days are the scoped store's (T18), else the business's.
  const bar = useListFilterBar({
    page: 'purchase_orders',
    keys: PO_FILTER_KEYS,
    date: PO_DATE,
    locationId: effectiveLocationId ?? null,
  });
  const { values } = bar;
  const hasFilters = Object.keys(values).some((k) => k !== 'location_id');
  const [isExporting, setIsExporting] = useState(false);

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;
  const isOwner = roleLevel >= ROLE_LEVELS.OWNER;

  // Auto-generate modal
  const [showAutoGenModal, setShowAutoGenModal] = useState(false);
  const [autoGenLocationId, setAutoGenLocationId] = useState(effectiveLocationId ?? '');
  const autoGenMutation = useAutoGeneratePO();

  // Locations for the scope filter and the auto-generate modal
  const { data: locationsData } = useQuery({
    queryKey: ['locations-list'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locations = locationsData?.data ?? [];

  // The "/" shortcut focuses this element directly. Looking the input up by its
  // placeholder text would couple the shortcut to display copy, so any reword or
  // translation of the placeholder would silently stop the shortcut from firing.
  const searchFieldRef = useRef<HTMLInputElement>(null);

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      next.set('page', '1');
      return next;
    });
  }

  const setPage = (p: number) => bar.onChange({ page: String(p) });

  const listParams = {
    status: values.status as PurchaseOrderStatus | undefined,
    search: values.search,
    supplier_name: values.supplier_name,
    mobile: values.mobile,
    po_number: values.po_number,
    start_date: values.start_date,
    end_date: values.end_date,
    location_id: effectiveLocationId,
  };
  const { data, isLoading, isError, error, refetch } = usePurchaseOrders({ ...listParams, page, limit: 20 });

  // Single aggregate request backs the KPI counts. Scoped to the same location as
  // the list so the cards can't describe a different store than the one selected.
  const { data: stats } = usePurchaseOrderStats({ location_id: effectiveLocationId });

  // One-click DAILY PO: an owner's is placed (ORDERED); a manager's is a DRAFT awaiting approval.
  const generateDaily = useMutation({
    mutationFn: () => purchaseOrdersApi.generateDailyPo(),
    onSuccess: (res) => {
      toast({
        title: (res.cost_to_confirm ?? 0) > 0
          ? `DAILY PO raised — ${res.po_number} (Draft: confirm the cost of ${res.cost_to_confirm} line(s), then approve)`
          : res.status === 'DRAFT'
            ? `DAILY PO raised — ${res.po_number} (Draft, awaiting owner approval)`
            : `DAILY PO placed — ${res.po_number} (Ordered)`,
        variant: 'success',
      });
      void refetch();
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });
  const onGenerateDaily = () => generateDaily.mutate();
  const onEditDaily = () => navigate('/purchase-orders/daily');

  useEffect(() => {
    if (error) {
      toast({ title: (error as Error).message, variant: 'error' });
    }
  }, [error]);

  // Every matching PO, all pages — the API writes the file (T27).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      downloadFile(await purchaseOrdersApi.exportCsv(listParams), 'purchase-orders.csv');
    } catch (err) {
      toast({ title: (err as Error).message || 'Export failed', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        searchFieldRef.current?.focus();
      }
      if (e.key === 'n' && isManager) {
        e.preventDefault();
        navigate('/inventory/purchase-orders/new');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, isManager]);

  function handleAutoGenerate() {
    if (!autoGenLocationId) {
      toast({ title: 'Please select a location', variant: 'error' });
      return;
    }
    autoGenMutation.mutate(
      { location_id: autoGenLocationId },
      {
        onSuccess: (result) => {
          toast({
            title: `Auto-generated PO ${result.po_number} with ${result.items.length} items`,
            variant: 'success',
          });
          for (const warning of result.unit_warnings ?? []) {
            toast({ title: `Unit changed — ${warning}`, variant: 'warning' });
          }
          setShowAutoGenModal(false);
          navigate(`/inventory/purchase-orders/${result.id}`);
        },
        onError: (err: Error) => {
          toast({ title: err.message, variant: 'error' });
        },
      },
    );
  }

  const purchaseOrders = data?.data ?? [];
  // Filtered list total — drives pagination and the empty state only. The KPI row
  // must stay filter-independent, so every card reads from the stats aggregate;
  // otherwise "Total POs" would drop below the status counts once a filter is applied.
  const total = data?.meta?.total ?? 0;
  const totalPos = stats?.total ?? 0;
  const draftCount = stats?.by_status?.DRAFT ?? 0;
  const orderedCount = stats?.by_status?.ORDERED ?? 0;
  const receivedCount = stats?.by_status?.RECEIVED ?? 0;

  const columns: ColumnDef<PurchaseOrderListItem>[] = [
    {
      key: 'po_number',
      header: 'PO #',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/inventory/purchase-orders/${row.id}`)}
          className="text-sm text-teal-600 dark:text-teal-400 hover:underline tabular-nums text-left font-medium"
        >
          {row.po_number}
        </button>
      ),
    },
    {
      key: 'supplier_name',
      header: 'Supplier',
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">
          {row.supplier_name}
        </span>
      ),
    },
    {
      key: 'location_name',
      header: 'Location',
      render: (_v, row) => (
        <span className="text-sm text-neutral-500 dark:text-neutral-400">
          {row.location_name}
        </span>
      ),
    },
    {
      key: 'item_count',
      header: 'Items',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">
          {row.item_count}
        </span>
      ),
    },
    {
      key: 'total_amount',
      header: 'Total',
      numeric: true,
      render: (_v, row) => (
        // A PO in a supplier's currency shows that currency, then the tenant figure.
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {row.currency_code ? formatDocAmount(row.total_amount, row.currency_code) : formatCurrency(row.total_amount, currency)}
          {row.currency_code && row.base_total_amount != null && (
            <span className="block text-xs font-normal text-neutral-500 dark:text-neutral-400">
              ≈ {formatCurrency(row.base_total_amount, currency)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => <POStatusBadge status={row.status} />,
    },
    {
      key: 'expected_delivery_date',
      header: 'Expected',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400 tabular-nums">
          {row.expected_delivery_date
            ? new Date(row.expected_delivery_date).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '\u2014'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400 tabular-nums">
          {new Date(row.created_at).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (_v, row) => (
        <div className="flex items-center justify-end gap-0.5">
          <button
            type="button"
            onClick={() => navigate(`/inventory/purchase-orders/${row.id}`)}
            className="p-1.5 rounded-lg text-neutral-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
            title="View"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => navigate(`/inventory/rfqs/new?from_po_id=${row.id}`)}
            className="p-1.5 rounded-lg text-neutral-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
            title="Create RFQ from this PO"
          >
            <ClipboardIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={56} className="rounded-xl mb-4" />
        <Skeleton variant="rect" height={400} className="rounded-xl" />
      </>
    );
  }

  // ── Load failure ──────────────────────────────────────────────────────────
  // MUST come before the empty state below. A failed list request leaves `total`
  // at 0 exactly like a tenant with no purchase orders, so without this branch a
  // 404/501 renders as "No purchase orders yet" — a broken page wearing a benign
  // "create your first purchase order" CTA. Retry re-runs the list query in place.

  if (isError) {
    return (
      <>
        <PoListHeader
          isManager={isManager}
          isOwner={isOwner}
          onNew={() => navigate('/inventory/purchase-orders/new')}
          onGenerateDaily={onGenerateDaily}
          onEditDaily={onEditDaily}
          generatingDaily={generateDaily.isPending}
        />

        <div className={`${GLASS} p-6 animate-fade-in`}>
          <Alert
            variant="danger"
            title="Failed to load purchase orders"
            description={
              error instanceof Error && error.message
                ? error.message
                : "We couldn't load your purchase orders. This is a problem reaching the server, not an empty list."
            }
            action={{ label: 'Retry', onClick: () => void refetch() }}
          />
        </div>
      </>
    );
  }

  // ── Empty state (no filters applied) ──────────────────────────────────────
  // `locationParam` counts as a filter: this branch renders without the filter bar,
  // so if an explicitly-scoped location happened to hold zero POs the user would be
  // stranded on "No purchase orders yet" with no dropdown left to widen the scope
  // back out. Fall through to the filterable view instead.

  if (!isLoading && total === 0 && !hasFilters && !locationParam) {
    return (
      <>
        <PoListHeader
          isManager={isManager}
          isOwner={isOwner}
          onNew={() => navigate('/inventory/purchase-orders/new')}
          onGenerateDaily={onGenerateDaily}
          onEditDaily={onEditDaily}
          generatingDaily={generateDaily.isPending}
        />

        <EmptyState
          variant="page"
          icon={<ClipboardIcon className="h-full w-full" />}
          title="No purchase orders yet"
          description="Create your first purchase order to track vendor procurement and manage stock replenishment."
          action={
            isManager
              ? { label: 'Create Purchase Order', onClick: () => navigate('/inventory/purchase-orders/new') }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <PoListHeader
        isManager={isManager}
        isOwner={isOwner}
        onNew={() => navigate('/inventory/purchase-orders/new')}
        onGenerateDaily={onGenerateDaily}
        onEditDaily={onEditDaily}
        generatingDaily={generateDaily.isPending}
        onAutoGenerate={() => {
          setAutoGenLocationId(effectiveLocationId ?? '');
          setShowAutoGenModal(true);
        }}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <PoKpi
          title="Total POs"
          value={totalPos.toLocaleString('en-IN')}
          tone="teal"
          icon={<ClipboardIcon className="h-5 w-5" />}
          delay={0}
        />
        <PoKpi
          title="Pending Approval"
          value={isOwner ? draftCount.toLocaleString('en-IN') : '\u2014'}
          tone="warning"
          icon={<ClockIcon className="h-5 w-5" />}
          masked={!isOwner}
          delay={60}
        />
        <PoKpi
          title="In Transit"
          value={isManager ? orderedCount.toLocaleString('en-IN') : '\u2014'}
          tone="brand"
          icon={<TruckIcon className="h-5 w-5" />}
          masked={!isManager}
          delay={120}
        />
        <PoKpi
          title="Received"
          value={isManager ? receivedCount.toLocaleString('en-IN') : '\u2014'}
          tone="success"
          icon={<CheckCircleIcon className="h-5 w-5" />}
          masked={!isManager}
          delay={180}
        />
      </div>

      {/* Filters (T27) — the shared bar, with this page's location scope beside it */}
      <div className="mb-4">
        <FilterBar
          facets={PO_FACETS}
          selects={[{ key: 'status', label: 'Status', options: STATUS_OPTIONS, allLabel: 'All statuses' }]}
          date={PO_DATE}
          values={values}
          onChange={bar.onChange}
          onReset={bar.onReset}
          timeZone={bar.timeZone}
          inputRef={searchFieldRef}
          placeholder="Type a supplier, mobile or PO number…  ( press / )"
          favorites={<SavedFiltersMenu {...bar.favorites} />}
          actions={
            <>
              {locations.length > 1 && (
                <div className="w-48">
                  <Select
                    options={[
                      { value: '', label: 'All Locations' },
                      ...locations.map((l) => ({ value: l.id, label: l.name })),
                    ]}
                    value={effectiveLocationId ?? ''}
                    onChange={(v) =>
                      setParam('location_id', (typeof v === 'string' && v) || ALL_LOCATIONS)
                    }
                    placeholder="All Locations"
                  />
                </div>
              )}
              <Button variant="secondary" onClick={handleExport} disabled={total === 0 || isExporting}>
                {isExporting ? 'Exporting…' : 'Export CSV'}
              </Button>
            </>
          }
        />
      </div>

      {/* Every matching PO, not the page — in the tenant's currency, as the export. */}
      <div className="mb-4">
        <ListSummaryBar
          loading={isLoading}
          items={[
            { label: 'POs matching', value: total.toLocaleString('en-IN'), tone: 'teal' },
            {
              label: 'Order value',
              value: formatCurrency(data?.meta?.summary?.total_amount ?? 0, currency),
              tone: 'success',
            },
          ]}
        />
      </div>

      {/* Data Table */}
      <div className={`${GLASS} animate-slide-up`}>
        <DataTable<PurchaseOrderListItem>
          columns={columns}
          data={purchaseOrders}
          loading={isLoading}
          totalCount={total}
          currentPage={page}
          pageSize={20}
          onPageChange={setPage}
          emptyState={{
            title: 'No purchase orders match filters',
            description: 'Try adjusting your filters or date range',
          }}
        />
      </div>

      {/* Auto-Generate Modal */}
      <Modal
        open={showAutoGenModal}
        onClose={() => setShowAutoGenModal(false)}
        title="Auto-Generate Purchase Order"
        allowOverflow
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowAutoGenModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAutoGenerate}
              disabled={autoGenMutation.isPending}
            >
              {autoGenMutation.isPending ? 'Generating...' : 'Generate PO'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Automatically create a purchase order for all products that have fallen below their
            low-stock threshold at the selected location.
          </p>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Location
            </label>
            <Select
              options={locations.map((l) => ({ value: l.id, label: l.name }))}
              value={autoGenLocationId}
              onChange={(v) => setAutoGenLocationId(typeof v === 'string' ? v : '')}
              placeholder="Select location"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function PurchaseOrderListPage() {
  return (
    <PageContainer
      title="Purchase Orders"
      breadcrumbs={[
        { label: 'Purchase' },
        { label: 'Purchase Orders' },
      ]}
    >
      <ErrorBoundary>
        <PurchaseOrderListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
